/**
 * Comprehensive validation utility based on CSV_Import_Field_Validation_Criteria.md
 * Provides validation rules for all form fields in the application
 */

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone validation regex (international formats, extensions, etc.)
const PHONE_REGEX = /^[\d\s\-+().,#xX;:/*a-zA-Z]+$/;

const hireStockRateMoney = (label) => ({
  required: false,
  type: 'money',
  min: 0.01,
  message: `${label} must be blank or greater than 0.01`,
});

const hireStockZeroMoney = (label) => ({
  required: false,
  type: 'money',
  min: 0,
  allowZero: true,
  message: `${label} must be blank or zero or greater`,
});

const HIRE_STOCK_PROP_VALIDATION_RULES = Object.fromEntries(
  Array.from({ length: 50 }, (_, i) => {
    const n = i + 1;
    return [
      `prop${n}`,
      { required: false, maxLength: 120, message: `Prop${n} max 120 characters` },
    ];
  })
);

const SALES_ITEMS_INDIVIDUAL_PROP_VALIDATION_RULES = Object.fromEntries(
  Array.from({ length: 50 }, (_, i) => {
    const n = i + 1;
    return [
      `prop${n}`,
      { required: false, maxLength: 120, message: `Prop${n} max 120 characters` },
    ];
  })
);

/**
 * Validation rules for different field types
 */
export const ValidationRules = {
  // Customer validation rules (based on Customers Import section)
  customer: {
    account_no: {
      required: false,
      maxLength: 8,
      unique: true,
      message: 'Account Number max 8 characters, must be unique. Leave blank for Cash Customer.',
    },
    name: {
      required: true,
      maxLength: 255,
      message: 'Customer Name is required',
    },
    company_type: {
      required: false,
      maxLength: 60,
      message: 'Company Type max 60 characters',
    },
    line_1: {
      required: true,
      maxLength: 50,
      message: 'Address Line 1 is mandatory, max 50 characters',
    },
    line_2: {
      required: false,
      maxLength: 50,
      message: 'Address Line 2 max 50 characters',
    },
    line_3: {
      required: false,
      maxLength: 50,
      message: 'Address Line 3 max 50 characters',
    },
    town: {
      required: false,
      maxLength: 50,
      message: 'Town max 50 characters',
    },
    county: {
      required: false,
      maxLength: 50,
      message: 'County max 50 characters',
    },
    postcode: {
      required: false,
      maxLength: 20,
      message: 'Postcode max 20 characters',
    },
    telephone: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Telephone max 50 characters, must be valid phone format',
    },
    fax: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Fax max 50 characters, must be valid phone format',
    },
    email: {
      required: false,
      maxLength: 128,
      pattern: EMAIL_REGEX,
      message: 'Email max 128 characters, must be valid email format',
    },
    contact_name: {
      required: false,
      maxLength: 60,
      message: 'Contact Name max 60 characters',
    },
    contact_telephone: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Contact Telephone max 50 characters, must be valid phone format',
    },
    website: {
      required: false,
      maxLength: 255,
      message: 'Website max 255 characters',
    },
    default_hire_period: {
      required: false,
      type: 'integer',
      min: 1,
      message: 'Default hire period must be a positive integer (number of days)',
    },
    company_reg_no: {
      required: false,
      maxLength: 20,
      message: 'Company Registration Number max 20 characters',
    },
    company_vat_no: {
      required: false,
      maxLength: 20,
      message: 'Company VAT Number max 20 characters',
    },
    company_cis_reg_no: {
      required: false,
      maxLength: 20,
      message: 'Company CIS Registration Number max 20 characters',
    },
    notes: {
      required: false,
      maxLength: 2000,
      message: 'Notes max 2000 characters',
    },
    type: {
      required: false,
      enum: ['C', 'A', 'P'],
      message: 'Type must be C (Cash), A (Account), or P (Prospect)',
    },
  },

  // Customer Sites validation rules (based on Customer Sites Import section)
  customerSite: {
    account_no: {
      required: true,
      maxLength: 20,
      foreignKey: 'customers',
      message: 'Customer Account Number is mandatory, max 20 characters, must exist in customers',
    },
    site_name: {
      required: true,
      maxLength: 50,
      uniquePerCustomer: true,
      message: 'Site Name is mandatory, max 50 characters, must be unique per customer',
    },
    line_1: {
      required: true,
      maxLength: 50,
      message: 'Address Line 1 is mandatory, max 50 characters',
    },
    line_2: {
      required: false,
      maxLength: 50,
      message: 'Address Line 2 max 50 characters',
    },
    line_3: {
      required: false,
      maxLength: 50,
      message: 'Address Line 3 max 50 characters',
    },
    town: {
      required: true,
      maxLength: 50,
      message: 'Town is mandatory, max 50 characters',
    },
    county: {
      required: false,
      maxLength: 50,
      message: 'County max 50 characters',
    },
    postcode: {
      required: false,
      maxLength: 20,
      message: 'Postcode max 20 characters',
    },
    country: {
      required: false,
      maxLength: 50,
      message: 'Country max 50 characters',
    },
    telephone: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Telephone max 50 characters, must be valid phone format',
    },
    fax: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Fax max 50 characters, must be valid phone format',
    },
    delivery_directions: {
      required: false,
      maxLength: 512,
      message: 'Delivery Directions max 512 characters',
    },
    start_date: {
      required: false,
      type: 'date',
      message: 'Start Date must be a valid date',
    },
    end_date: {
      required: false,
      type: 'date',
      afterField: 'start_date',
      message: 'End Date must be a valid date and after start date',
    },
    latitude: {
      required: false,
      maxLength: 30,
      type: 'number',
      min: -90,
      max: 90,
      message: 'Latitude max 30 characters, must be valid GPS coordinate (-90 to 90)',
    },
    longitude: {
      required: false,
      maxLength: 30,
      type: 'number',
      min: -180,
      max: 180,
      message: 'Longitude max 30 characters, must be valid GPS coordinate (-180 to 180)',
    },
    area: {
      required: false,
      maxLength: 60,
      message: 'Area max 60 characters',
    },
    route: {
      required: false,
      maxLength: 60,
      message: 'Route max 60 characters',
    },
    depot: {
      required: false,
      message: 'Depot must exist in depots table',
    },
  },

  // Staff Members validation rules (based on Staff Members Import section)
  staff: {
    short_name: {
      required: false,
      maxLength: 20,
      message: 'Short Name max 20 characters',
    },
    first_name: {
      required: true,
      maxLength: 50,
      message: 'First Name is mandatory, max 50 characters',
    },
    surname: {
      required: true,
      maxLength: 50,
      message: 'Surname is mandatory, max 50 characters',
    },
    employee_number: {
      required: false,
      maxLength: 20,
      message: 'Employee Number max 20 characters',
    },
    notes: {
      required: false,
      maxLength: 2000,
      message: 'Notes max 2000 characters',
    },
    cost_per_hour: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Cost Per Hour must be blank or greater than 0.01',
    },
    workshop_recharge_per_hour: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Workshop Recharge Per Hour must be blank or greater than 0.01',
    },
  },

  // Hire Category Groups validation rules
  hireCategoryGroup: {
    group_name: {
      required: true,
      maxLength: 60,
      message: 'Group Name is mandatory, max 60 characters',
    },
    description: {
      required: false,
      maxLength: 120,
      message: 'Description max 120 characters',
    },
    current_flag: {
      required: false,
      type: 'boolean',
      default: true,
      message: 'Current Flag must be boolean (true/false)',
    },
  },

  // Hire Categories validation rules
  hireCategory: {
    category_class_code: {
      required: true,
      maxLength: 30,
      unique: true,
      message: 'Category Class Code is mandatory, max 30 characters, must be unique',
    },
    category_description: {
      required: false,
      maxLength: 120,
      message: 'Category Description max 120 characters',
    },
    hire_category_group: {
      required: false,
      maxLength: 60,
      foreignKey: 'hireCategoryGroups',
      caseSensitive: true,
      message:
        'Hire Category Group max 60 characters, must exist in hire category groups (case sensitive)',
    },
  },

  // Sales Categories validation rules
  salesCategory: {
    sales_category_class: {
      required: true,
      maxLength: 30,
      unique: true,
      message: 'Sales Category Class is mandatory, max 30 characters, must be unique',
    },
    category_description: {
      required: false,
      maxLength: 120,
      message: 'Category Description max 120 characters',
    },
    current_flag: {
      required: false,
      type: 'boolean',
      default: true,
      message: 'Current Flag must be boolean (true/false)',
    },
  },

  // Sales Items validation rules
  salesItem: {
    main_stock_number: {
      required: true,
      maxLength: 20,
      unique: true,
      message: 'Main Stock Number is mandatory, max 20 characters, must be unique',
    },
    serial_number: {
      required: false,
      maxLength: 50,
      message: 'Serial Number max 50 characters',
    },
    item_name: {
      required: false,
      maxLength: 120,
      message: 'Item Name max 120 characters',
    },
    purchase_price: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Purchase Price must be blank or greater than 0.01',
    },
    purchase_date: {
      required: false,
      type: 'date',
      notFuture: true,
      message: 'Purchase Date must be valid date and not in future',
    },
    sale_price: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Sale Price must be blank or greater than 0.01',
    },
    make: {
      required: false,
      maxLength: 30,
      message: 'Make max 30 characters',
    },
    model: {
      required: false,
      maxLength: 128,
      message: 'Model max 128 characters',
    },
    create_inspections_flag: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Create Inspections Flag must be boolean (true/false)',
    },
  },

  // Inclusions validation rules
  inclusion: {
    parent_type: {
      required: true,
      enum: ['C', 'S'],
      message: 'Parent Type is mandatory and must be "C" (Category) or "S" (Stock)',
    },
    parent_code: {
      required: true,
      maxLength: 255,
      message:
        'Parent Code is mandatory. Must be a valid hire/sales category code (if Parent Type is "C") or hire/sales stock number (if Parent Type is "S")',
    },
    incl_cat: {
      required: true,
      maxLength: 255,
      message: 'Inclusion Category is mandatory',
    },
    incl_stockno: {
      required: false,
      maxLength: 255,
      message:
        'Inclusion Stock Number max 255 characters. If provided, must exist in hire stock or sales items, and its category must match the Inclusion Category',
    },
    sales_inclusion_method: {
      required: false,
      conditionallyRequired: true, // Required only if incl_cat is a sales category
      enum: ['1', '2', '3', '4'],
      message:
        'Sales Inclusion Method is required when Inclusion Category is a sales category. Must be 1, 2, 3, or 4',
    },
    order_num: {
      required: false,
      type: 'integer',
      message: 'Order Number must be a whole number',
    },
    quantity: {
      required: false,
      type: 'number',
      min: 0,
      message: 'Quantity must be a positive number',
    },
    include_flag: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Include Flag must be boolean (true/false)',
    },
    no_charge: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'No Charge Flag must be boolean (true/false)',
    },
    sales_inclusion_method: {
      required: false,
      enum: ['1', '2', '3', '4'],
      conditionallyRequired: true,
      message:
        'Sales Inclusion Method is required when Inclusion Category is a sales category. Must be 1, 2, 3, or 4',
    },
  },

  // Fleet Reminders validation rules
  fleetReminder: {
    stock_no: {
      required: true,
      maxLength: 20,
      foreignKey: 'hireItems',
      message: 'Stock Number is mandatory, max 20 characters, must exist in hire items',
    },
    service_name: {
      required: true,
      maxLength: 50,
      message: 'Service Name is mandatory, max 50 characters',
    },
    due_date: {
      required: false,
      type: 'date',
      message: 'Due Date must be a valid date',
    },
    due_meter: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Due Meter must be a whole number zero or greater',
    },
    due_notes: {
      required: false,
      maxLength: 255,
      message: 'Due Notes max 255 characters',
    },
    inspection_name: {
      required: false,
      maxLength: 255,
      message: 'Inspection Name max 255 characters',
    },
    last_test_date: {
      required: false,
      type: 'date',
      message: 'Last Test Date must be a valid date',
    },
    last_test_no: {
      required: false,
      maxLength: 255,
      message: 'Last Test No max 255 characters',
    },
    during_hire_only: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'During Hire Only must be boolean (true/false)',
    },
  },

  salesItemsIndividual: {
    main_stock_no: {
      required: true,
      maxLength: 20,
      message: 'Main Stock Number is mandatory, max 20 characters',
    },
    serial_no: {
      required: false,
      maxLength: 50,
      message: 'Serial Number max 50 characters',
    },
    name: {
      required: true,
      maxLength: 120,
      message: 'Name is mandatory, max 120 characters',
    },
    purch_price: hireStockZeroMoney('Purchase Price'),
    purch_date: {
      required: false,
      type: 'date',
      notFuture: true,
      message: 'Purchase Date must be valid and not in the future',
    },
    sale_price: hireStockZeroMoney('Sale Price'),
    depot: {
      required: false,
      maxLength: 20,
      message: 'Depot max 20 characters',
    },
    make: {
      required: false,
      maxLength: 30,
      message: 'Make max 30 characters',
    },
    model: {
      required: false,
      maxLength: 128,
      message: 'Model max 128 characters',
    },
    ...SALES_ITEMS_INDIVIDUAL_PROP_VALIDATION_RULES,
  },

  fleetServiceHistory: {
    stock_no: {
      required: true,
      maxLength: 20,
      message: 'Stock Number is mandatory, max 20 characters',
    },
    type: {
      required: true,
      maxLength: 50,
      message: 'Type is mandatory, max 50 characters',
    },
    service_date: {
      required: true,
      type: 'date',
      message: 'Service Date is mandatory and must be a valid date',
    },
    notes: {
      required: true,
      maxLength: 2000,
      message: 'Notes are mandatory, max 2000 characters',
    },
    cost_parts: hireStockZeroMoney('Cost Parts'),
    cost_labour: hireStockZeroMoney('Cost Labour'),
    meter_reading: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Meter Reading must be blank or zero or greater',
    },
  },

  meterReading: {
    fleet_no: {
      required: true,
      maxLength: 20,
      message: 'Fleet Number is mandatory, max 20 characters',
    },
    reading_date: {
      required: true,
      type: 'date',
      message: 'Reading Date is mandatory and must be a valid date',
    },
    meter: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Meter must be blank or zero or greater',
    },
    fuel_stock_no: {
      required: false,
      maxLength: 20,
      message: 'Fuel Stock Number max 20 characters',
    },
    fuel_qty: {
      required: false,
      type: 'number',
      min: 0,
      message: 'Fuel Quantity must be blank or zero or greater',
    },
    fuel_unit_cost: hireStockZeroMoney('Fuel Unit Cost'),
  },

  customerEquipment: {
    equipment_name: {
      required: true,
      maxLength: 120,
      message: 'Equipment Name is mandatory, max 120 characters',
    },
    account_no: {
      required: false,
      maxLength: 20,
      message: 'Account Number max 20 characters',
    },
    make: {
      required: false,
      maxLength: 30,
      message: 'Make max 30 characters',
    },
    model: {
      required: false,
      maxLength: 128,
      message: 'Model max 128 characters',
    },
    power: {
      required: false,
      maxLength: 60,
      message: 'Power max 60 characters',
    },
    serial_no: {
      required: false,
      maxLength: 50,
      message: 'Serial Number max 50 characters',
    },
    reg_no: {
      required: false,
      maxLength: 30,
      message: 'Reg Number max 30 characters',
    },
    fleet_no: {
      required: false,
      maxLength: 20,
      message: 'Fleet Number max 20 characters',
    },
    cat: {
      required: false,
      maxLength: 12,
      message: 'Category max 12 characters',
    },
    purch_date: {
      required: false,
      type: 'date',
      message: 'Purchase Date must be a valid date',
    },
    additional_info: {
      required: false,
      maxLength: 512,
      message: 'Additional Info max 512 characters',
    },
    year_of_manufacture: {
      required: false,
      type: 'integer',
      min: 1900,
      max: 2100,
      message: 'Year of Manufacture must be between 1900 and 2100',
    },
    service_name: {
      required: false,
      maxLength: 60,
      message: 'Service Name max 60 characters',
    },
    next_serv_date: {
      required: false,
      type: 'date',
      message: 'Next Service Date must be a valid date',
    },
    next_serv_meter: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Next Service Meter must be blank or zero or greater',
    },
    next_serv_notes: {
      required: false,
      maxLength: 512,
      message: 'Next Service Notes max 512 characters',
    },
    site_name: {
      required: false,
      maxLength: 50,
      message: 'Site Name max 50 characters',
    },
    inspection_test: {
      required: false,
      maxLength: 60,
      message: 'Inspection Test max 60 characters',
    },
  },

  liveContract: {
    contract_no: {
      required: true,
      maxLength: 20,
      message: 'Contract No is mandatory, max 20 characters',
    },
    account_no: {
      required: true,
      maxLength: 20,
      message: 'Account No is mandatory, max 20 characters',
    },
    site_name: {
      required: true,
      maxLength: 50,
      message: 'Site Name is mandatory, max 50 characters',
    },
    line_1: { required: false, maxLength: 50, message: 'Line 1 max 50 characters' },
    line_2: { required: false, maxLength: 50, message: 'Line 2 max 50 characters' },
    town: { required: false, maxLength: 50, message: 'Town max 50 characters' },
    county: { required: false, maxLength: 50, message: 'County max 50 characters' },
    postcode: { required: false, maxLength: 8, message: 'Postcode max 8 characters' },
    start_date: { required: true, type: 'date', message: 'Start Date is mandatory' },
    order_no: { required: false, maxLength: 30, message: 'Order No max 30 characters' },
    contact_name: { required: false, maxLength: 30, message: 'Contact Name max 30 characters' },
    depot: { required: false, maxLength: 3, message: 'Depot max 3 characters' },
    cat: { required: true, maxLength: 12, message: 'Cat is mandatory, max 12 characters' },
    stock_no: { required: false, maxLength: 20, message: 'Stock No max 20 characters' },
    description: {
      required: true,
      maxLength: 128,
      message: 'Description is mandatory, max 128 characters',
    },
    quantity: {
      required: false,
      type: 'number',
      min: 0.01,
      message: 'Quantity must be at least 0.01 when provided',
    },
    charge_rate: hireStockZeroMoney('Charge Rate'),
    charge_period: {
      required: false,
      enum: [
        '5WEEK',
        '6WEEK',
        '7WEEK',
        'HOUR',
        'SC',
        'W/E',
        'YEAR',
        'MONTH',
        'DAY',
        '5DAY',
        '6DAY',
        '7DAY',
      ],
      message: 'Charge Period must be a valid hire period',
    },
    discount_pct: {
      required: false,
      type: 'number',
      min: 1,
      max: 99,
      message: 'Discount % must be between 1 and 99 when provided',
    },
    orig_start_date: {
      required: false,
      type: 'date',
      message: 'Orig Start Date must be a valid date',
    },
    orig_start_time: {
      required: false,
      pattern: /^([01]?\d|2[0-3]):[0-5]\d$/,
      message: 'Orig Start Time must be HH:MM',
    },
    off_hire_date: { required: false, type: 'date', message: 'Off Hire Date must be a valid date' },
    off_hire_time: {
      required: false,
      pattern: /^([01]?\d|2[0-3]):[0-5]\d$/,
      message: 'Off Hire Time must be HH:MM',
    },
    notes: { required: false, maxLength: 512, message: 'Notes max 512 characters' },
    planned_end: { required: false, type: 'date', message: 'Planned End must be a valid date' },
  },

  // Inspection Tests validation rules
  inspectionTest: {
    inspection_name: {
      required: true,
      maxLength: 60,
      message: 'Inspection Name is mandatory, max 60 characters',
    },
    item_name: {
      required: true,
      maxLength: 100,
      message: 'Item Name is mandatory, max 100 characters',
    },
    expiry_days: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Expiry Days must be a positive integer (number of days)',
    },
    print_on_confirm: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Print on Confirm must be boolean (true/false)',
    },
    raise_job: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Raise Job must be boolean (true/false)',
    },
    check_for: {
      required: false,
      maxLength: 200,
      message: 'Check For max 200 characters',
    },
    requires_value: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Requires Value must be boolean (true/false)',
    },
    low_value: {
      required: false,
      type: 'number',
      requiresField: 'requires_value',
      message:
        'Low Value must be a valid number and can only be specified when "Requires Value?" is checked',
    },
    high_value: {
      required: false,
      type: 'number',
      requiresField: 'requires_value',
      message:
        'High Value must be a valid number and can only be specified when "Requires Value?" is checked',
    },
    value_units: {
      required: false,
      maxLength: 20,
      message: 'Value Units max 20 characters',
    },
    print: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Print must be boolean (true/false)',
    },
    help_notes: {
      required: false,
      maxLength: 500,
      message: 'Help Notes max 500 characters',
    },
  },

  // Customer Contacts validation rules (based on customer_contacts.csv)
  customerContact: {
    account_no: {
      required: true,
      maxLength: 10,
      foreignKey: 'customers',
      message: 'Account Number is mandatory, max 10 characters, must exist in customers',
    },
    title: {
      required: false,
      maxLength: 10,
      message: 'Title max 10 characters',
    },
    first_name: {
      required: false,
      maxLength: 60,
      message: 'First Name max 60 characters',
    },
    surname: {
      required: true,
      maxLength: 60,
      message: 'Surname is mandatory, max 60 characters',
    },
    telephone: {
      required: false,
      maxLength: 60,
      pattern: PHONE_REGEX,
      message: 'Telephone max 60 characters, must be valid phone format',
    },
    fax: {
      required: false,
      maxLength: 60,
      pattern: PHONE_REGEX,
      message: 'Fax max 60 characters, must be valid phone format',
    },
    mobile: {
      required: false,
      maxLength: 60,
      pattern: PHONE_REGEX,
      message: 'Mobile max 60 characters, must be valid phone format',
    },
    email: {
      required: false,
      maxLength: 128,
      pattern: EMAIL_REGEX,
      message: 'Email max 128 characters, must be valid email format',
    },
    main: {
      required: false,
      type: 'boolean',
      default: true,
      message: 'Main contact flag (defaults to Yes)',
    },
    marketing: {
      required: false,
      type: 'boolean',
      default: true,
      message: 'Marketing flag (defaults to Yes)',
    },
    job_title: {
      required: false,
      maxLength: 60,
      message: 'Job Title max 60 characters',
    },
    comm_method: {
      required: false,
      maxLength: 60,
      message: 'Communication Method max 60 characters',
    },
    salutation: {
      required: false,
      maxLength: 60,
      message: 'Salutation max 60 characters',
    },
    site_name: {
      required: false,
      maxLength: 50,
      message: 'Site Name max 50 characters',
    },
    notes: {
      required: false,
      maxLength: 4000,
      message: 'Notes max 4000 characters',
    },
  },

  // Nominal Codes validation rules
  nominalCode: {
    type: {
      required: true,
      enum: ['S', 'P', 'PL'],
      message: 'Type is mandatory and must be S (Sales), P (Purchase), or PL (Profit & Loss)',
    },
    code: {
      required: true,
      maxLength: 20,
      unique: true,
      message: 'Code is mandatory, max 20 characters, must be unique',
    },
    name: {
      required: true,
      maxLength: 60,
      message: 'Name is mandatory, max 60 characters',
    },
    name_on_invoice: {
      required: false,
      type: 'boolean',
      default: false,
      message: 'Name on Invoice must be boolean (true/false)',
    },
  },

  // Suppliers validation rules
  supplier: {
    code: {
      required: true,
      maxLength: 8,
      unique: true,
      message: 'Account Number is mandatory, max 8 characters, must be unique',
    },
    name: {
      required: true,
      maxLength: 20,
      message: 'Supplier Name is mandatory, max 20 characters',
    },
    line_1: {
      required: true,
      maxLength: 50,
      message: 'Address Line 1 is mandatory, max 50 characters',
    },
    line_2: {
      required: false,
      maxLength: 50,
      message: 'Address Line 2 max 50 characters',
    },
    line_3: {
      required: false,
      maxLength: 50,
      message: 'Address Line 3 max 50 characters',
    },
    town: {
      required: true,
      maxLength: 50,
      message: 'Town is mandatory, max 50 characters',
    },
    county: {
      required: false,
      maxLength: 50,
      message: 'County max 50 characters',
    },
    postcode: {
      required: false,
      maxLength: 20,
      message: 'Postcode max 20 characters',
    },
    telephone: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Telephone max 50 characters, must be valid phone format',
    },
    fax: {
      required: false,
      maxLength: 50,
      pattern: PHONE_REGEX,
      message: 'Fax max 50 characters, must be valid phone format',
    },
    email: {
      required: false,
      maxLength: 128,
      pattern: EMAIL_REGEX,
      message: 'Email max 128 characters, must be valid email format',
    },
    contact_name: {
      required: false,
      maxLength: 60,
      message: 'Contact Name max 60 characters',
    },
    website: {
      required: false,
      maxLength: 255,
      message: 'Website max 255 characters',
    },
  },

  // Hire Stock Individual validation rules
  hireStock: {
    cat: {
      required: true,
      maxLength: 12,
      foreignKey: 'hireCategories',
      message: 'Category is mandatory, max 12 characters, must be a valid hire category',
    },
    stock_no: {
      required: true,
      maxLength: 20,
      unique: true,
      message: 'Stock Number is mandatory, max 20 characters, must be unique',
    },
    name: {
      required: true,
      maxLength: 120,
      message: 'Name is mandatory, max 120 characters',
    },
    main_stock_number: {
      required: true,
      maxLength: 20,
      unique: true,
      message: 'Main Stock Number is mandatory, max 20 characters, must be unique',
    },
    serial_number: {
      required: false,
      maxLength: 50,
      message: 'Serial Number max 50 characters',
    },
    serial_no: {
      required: false,
      maxLength: 50,
      message: 'Serial Number max 50 characters',
    },
    item_name: {
      required: false,
      maxLength: 120,
      message: 'Item Name max 120 characters',
    },
    purchase_price: hireStockZeroMoney('Purchase Price'),
    purch_price: hireStockZeroMoney('Purchase Price'),
    purchase_date: {
      required: false,
      type: 'date',
      notFuture: true,
      message: 'Purchase Date must be valid date and not in future',
    },
    purch_date: {
      required: false,
      type: 'date',
      message: 'Purchase Date must be a valid date',
    },
    daily_hire_rate: hireStockRateMoney('Daily Hire Rate'),
    weekly_hire_rate: hireStockRateMoney('Weekly Hire Rate'),
    week_rate: hireStockRateMoney('Week Rate'),
    one_day_rate: hireStockRateMoney('One Day Rate'),
    two_day_rate: hireStockRateMoney('Two Day Rate'),
    three_day_rate: hireStockRateMoney('Three Day Rate'),
    four_day_rate: hireStockRateMoney('Four Day Rate'),
    part_day_rate: hireStockRateMoney('Part Day Rate'),
    weekend_rate: hireStockRateMoney('Weekend Rate'),
    hour_rate: hireStockRateMoney('Hour Rate'),
    day_rate: hireStockRateMoney('Day Rate'),
    month_rate: hireStockRateMoney('Month Rate'),
    sc_rate: hireStockRateMoney('SC Rate'),
    unit_rate: hireStockRateMoney('Unit Rate'),
    switch_week_rate: hireStockRateMoney('Switch Week Rate'),
    switch_after_weeks: {
      required: false,
      type: 'integer',
      min: 1,
      message: 'Switch After Weeks must be blank or at least 1',
    },
    deposit: hireStockRateMoney('Deposit'),
    sale_date: {
      required: false,
      type: 'date',
      message: 'Sale Date must be a valid date',
    },
    sale_value: hireStockZeroMoney('Sale Value'),
    sale_invoice: {
      required: false,
      maxLength: 20,
      message: 'Sale Invoice max 20 characters',
    },
    provisional_type: {
      required: false,
      maxLength: 20,
      enum: ['BORROWED', 'CROSS-HIRE', 'LEASED', 'SALE-STOCK'],
      message: 'Provisional Type must be BORROWED, CROSS-HIRE, LEASED, or SALE-STOCK',
    },
    reg_number: {
      required: false,
      maxLength: 30,
      message: 'Reg Number max 30 characters',
    },
    nom_code: {
      required: false,
      maxLength: 20,
      message: 'Nom Code must be a valid Sales nominal code, max 20 characters',
    },
    depreciation_rate: {
      required: false,
      maxLength: 5,
      message: 'Depreciation Rate max 5 characters',
    },
    supplier: {
      required: false,
      maxLength: 60,
      message: 'Supplier max 60 characters',
    },
    additional_info: {
      required: false,
      maxLength: 512,
      message: 'Additional Info max 512 characters',
    },
    last_meter_rdg: {
      required: false,
      type: 'integer',
      min: 0,
      message: 'Last Meter Reading must be blank or zero or greater',
    },
    hire_notes: {
      required: false,
      maxLength: 512,
      message: 'Hire Notes max 512 characters',
    },
    inspection_test: {
      required: false,
      maxLength: 60,
      message: 'Inspection Test max 60 characters',
    },
    warranty_type: {
      required: false,
      maxLength: 60,
      message: 'Warranty Type max 60 characters',
    },
    warranty_supplier: {
      required: false,
      maxLength: 255,
      message: 'Warranty Supplier must be a valid supplier account number',
    },
    warranty_start_date: {
      required: false,
      type: 'date',
      message: 'Warranty Start Date must be a valid date',
    },
    barcode: {
      required: false,
      maxLength: 20,
      message: 'Barcode max 20 characters',
    },
    rfid: {
      required: false,
      maxLength: 20,
      message: 'RFID max 20 characters',
    },
    qr_code: {
      required: false,
      maxLength: 50,
      message: 'QR Code max 50 characters',
    },
    alert_text: {
      required: false,
      maxLength: 512,
      message: 'Alert Text max 512 characters',
    },
    on_hp: {
      required: true,
      type: 'boolean',
      message: 'On HP? is required',
    },
    hp_notes: {
      required: false,
      maxLength: 30,
      message: 'HP Notes max 30 characters',
    },
    hp_over_months: {
      required: false,
      type: 'integer',
      min: 1,
      message: 'HP Over Months must be blank or at least 1',
    },
    sale_price: hireStockRateMoney('Sale Price'),
    insurance_value: hireStockRateMoney('Insurance Value'),
    previous_income: hireStockZeroMoney('Previous Income'),
    previous_cost_parts: hireStockRateMoney('Previous Cost Parts'),
    previous_cost_labour: hireStockRateMoney('Previous Cost Labour'),
    fixed_monthly_cost: hireStockRateMoney('Fixed Monthly Cost'),
    anticipated_weekly_income: hireStockRateMoney('Anticipated Weekly Income'),
    weight: {
      required: false,
      type: 'number',
      min: 0.01,
      message: 'Weight must be blank or at least 0.01',
    },
    avg_purch_price: hireStockRateMoney('Average Purchase Price'),
    make: {
      required: false,
      maxLength: 30,
      message: 'Make max 30 characters',
    },
    model: {
      required: false,
      maxLength: 128,
      requiresField: 'make',
      message: 'Model max 128 characters, requires valid make',
    },
    ...HIRE_STOCK_PROP_VALIDATION_RULES,
  },

  // Hire Stock Bulk validation rules
  hireStockBulk: {
    cat: {
      required: true,
      maxLength: 255,
      message: 'Category is mandatory',
    },
    stock_no: {
      required: true,
      maxLength: 255,
      message: 'Stock Number is mandatory',
    },
    name: {
      required: true,
      maxLength: 255,
      message: 'Name is mandatory',
    },
    sale_price: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Sale Price must be blank or greater than 0.01',
    },
    week_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Week Rate must be blank or greater than 0.01',
    },
    one_day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'One Day Rate must be blank or greater than 0.01',
    },
    two_day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Two Day Rate must be blank or greater than 0.01',
    },
    three_day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Three Day Rate must be blank or greater than 0.01',
    },
    four_day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Four Day Rate must be blank or greater than 0.01',
    },
    part_day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Part Day Rate must be blank or greater than 0.01',
    },
    weekend_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Weekend Rate must be blank or greater than 0.01',
    },
    hour_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Hour Rate must be blank or greater than 0.01',
    },
    day_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Day Rate must be blank or greater than 0.01',
    },
    month_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Month Rate must be blank or greater than 0.01',
    },
    sc_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'SC Rate must be blank or greater than 0.01',
    },
    unit_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Unit Rate must be blank or greater than 0.01',
    },
    switch_week_rate: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Switch Week Rate must be blank or greater than 0.01',
    },
    deposit: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Deposit must be blank or greater than 0.01',
    },
    purch_price: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Purchase Price must be blank or greater than 0.01',
    },
    avg_purch_price: {
      required: false,
      type: 'money',
      min: 0.01,
      message: 'Average Purchase Price must be blank or greater than 0.01',
    },
  },
};

/**
 * Validate a single field value against its rules
 * @param {string} fieldName - The name of the field
 * @param {any} value - The value to validate
 * @param {object} rules - The validation rules for this field
 * @param {object} allData - All form data (for cross-field validation)
 * @returns {string|null} - Error message or null if valid
 */
export function validateField(fieldName, value, rules, allData = {}) {
  if (!rules) return null;

  // Convert empty strings to null for validation
  const normalizedValue = value === '' ? null : value;

  // Required field validation
  if (rules.required && (normalizedValue === null || normalizedValue === undefined)) {
    return rules.message || `${fieldName} is required`;
  }

  // Skip other validations if value is empty and not required
  if (normalizedValue === null || normalizedValue === undefined) {
    return null;
  }

  // String length validation
  if (
    rules.maxLength &&
    typeof normalizedValue === 'string' &&
    normalizedValue.length > rules.maxLength
  ) {
    return rules.message || `${fieldName} must be ${rules.maxLength} characters or less`;
  }

  // Pattern validation (regex)
  if (
    rules.pattern &&
    typeof normalizedValue === 'string' &&
    !rules.pattern.test(normalizedValue)
  ) {
    return rules.message || `${fieldName} format is invalid`;
  }

  // Enum validation
  if (rules.enum && !rules.enum.includes(normalizedValue)) {
    return rules.message || `${fieldName} must be one of: ${rules.enum.join(', ')}`;
  }

  // Number type validation
  if (rules.type === 'number' || rules.type === 'money' || rules.type === 'integer') {
    const numValue = parseFloat(normalizedValue);
    if (isNaN(numValue)) {
      return rules.message || `${fieldName} must be a valid number`;
    }

    if (rules.type === 'integer' && !Number.isInteger(numValue)) {
      return rules.message || `${fieldName} must be a whole number`;
    }

    // For money fields, reject exactly 0 unless zero is explicitly allowed
    if (rules.type === 'money' && numValue === 0 && !rules.allowZero) {
      return `${fieldName} cannot be 0. Please leave blank or enter a value greater than 0.01`;
    }

    if (rules.min !== undefined && numValue < rules.min) {
      return rules.message || `${fieldName} must be ${rules.min} or greater`;
    }

    if (rules.max !== undefined && numValue > rules.max) {
      return rules.message || `${fieldName} must be ${rules.max} or less`;
    }
  }

  // Date validation
  if (rules.type === 'date') {
    const dateValue = new Date(normalizedValue);
    if (isNaN(dateValue.getTime())) {
      return rules.message || `${fieldName} must be a valid date`;
    }

    if (rules.notFuture && dateValue > new Date()) {
      return rules.message || `${fieldName} cannot be in the future`;
    }

    if (rules.afterField && allData[rules.afterField]) {
      const afterDate = new Date(allData[rules.afterField]);
      if (!isNaN(afterDate.getTime()) && dateValue <= afterDate) {
        return rules.message || `${fieldName} must be after ${rules.afterField}`;
      }
    }
  }

  // Boolean validation
  if (rules.type === 'boolean') {
    if (
      typeof normalizedValue !== 'boolean' &&
      normalizedValue !== 'true' &&
      normalizedValue !== 'false' &&
      normalizedValue !== 0 &&
      normalizedValue !== 1
    ) {
      return rules.message || `${fieldName} must be true or false`;
    }
  }

  // Cross-field validation - check if required field is set
  if (rules.requiresField) {
    const requiredFieldValue = allData[rules.requiresField];
    // For boolean fields, check if explicitly true
    // For other fields, check if truthy and not empty string
    const isBooleanField =
      typeof requiredFieldValue === 'boolean' ||
      requiredFieldValue === 'true' ||
      requiredFieldValue === 'false' ||
      requiredFieldValue === true ||
      requiredFieldValue === false;

    if (isBooleanField) {
      // For boolean dependencies, the field must be true
      if (
        requiredFieldValue !== true &&
        requiredFieldValue !== 'true' &&
        requiredFieldValue !== 1 &&
        requiredFieldValue !== '1'
      ) {
        // Only validate if the current field has a value
        if (value !== null && value !== undefined && value !== '') {
          return (
            rules.message ||
            `${fieldName} can only be specified when ${rules.requiresField} is checked`
          );
        }
      }
    } else {
      // For non-boolean fields, just check if set
      if (!requiredFieldValue || requiredFieldValue === '') {
        if (value !== null && value !== undefined && value !== '') {
          return rules.message || `${fieldName} requires ${rules.requiresField} to be specified`;
        }
      }
    }
  }

  return null;
}

const FLEET_REMINDER_DUE_MESSAGE = 'Either Due Date or Due Meter is required';

function hasFleetReminderDueDate(formData) {
  const value = formData.due_date;
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function hasFleetReminderDueMeter(formData) {
  const value = formData.due_meter;
  return value !== null && value !== undefined && String(value).trim() !== '';
}

export function validateSalesItemIndividualForm(formData) {
  return validateForm(formData, ValidationRules.salesItemsIndividual);
}

export function validateFleetServiceHistoryForm(formData) {
  return validateForm(formData, ValidationRules.fleetServiceHistory);
}

export function validateMeterReadingForm(formData) {
  return validateForm(formData, ValidationRules.meterReading);
}

export function validateCustomerEquipmentForm(formData) {
  return validateForm(formData, ValidationRules.customerEquipment);
}

export function validateLiveContractForm(formData) {
  return validateForm(formData, ValidationRules.liveContract);
}

/**
 * Validate fleet reminder form including either due_date or due_meter required.
 */
export function validateFleetReminderForm(formData) {
  const errors = validateForm(formData, ValidationRules.fleetReminder);

  if (!hasFleetReminderDueDate(formData) && !hasFleetReminderDueMeter(formData)) {
    errors.due_date = FLEET_REMINDER_DUE_MESSAGE;
    errors.due_meter = FLEET_REMINDER_DUE_MESSAGE;
  }

  return errors;
}

/**
 * Validate all fields in a form data object
 * @param {object} formData - The form data to validate
 * @param {object} validationRules - The validation rules object
 * @returns {object} - Object with field names as keys and error messages as values
 */
export function validateForm(formData, validationRules) {
  const errors = {};

  Object.keys(validationRules).forEach((fieldName) => {
    const error = validateField(
      fieldName,
      formData[fieldName],
      validationRules[fieldName],
      formData
    );
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
}

/**
 * Check if form has any validation errors
 * @param {object} errors - Errors object from validateForm
 * @returns {boolean} - True if there are errors
 */
export function hasErrors(errors) {
  return Object.keys(errors).length > 0;
}

/**
 * Get the first error message from errors object
 * @param {object} errors - Errors object from validateForm
 * @returns {string|null} - First error message or null
 */
export function getFirstError(errors) {
  const errorKeys = Object.keys(errors);
  return errorKeys.length > 0 ? errors[errorKeys[0]] : null;
}

/**
 * Format validation errors for display
 * @param {object} errors - Errors object from validateForm
 * @returns {string} - Formatted error message
 */
export function formatErrors(errors) {
  const errorMessages = Object.values(errors);
  if (errorMessages.length === 0) return '';
  if (errorMessages.length === 1) return errorMessages[0];
  return `Multiple validation errors:\n• ${errorMessages.join('\n• ')}`;
}
